import {
    getPayrollConfig as repoGetPayrollConfig,
    savePayrollConfig as repoSavePayrollConfig,
    savePeriodoPorDefecto as repoSavePeriodoPorDefecto,
} from '../infrastructure/payroll-config.repository.js';

/** S4 (AD17, design.md): valores válidos de `periodo_por_defecto` — mismo CHECK que migración 28. */
const PERIODO_POR_DEFECTO_VALIDOS = ['mes', 'quincena', 'semana'];

/**
 * Servicio del editor admin del tope mensual de descuento (`payroll_config`,
 * AD11, slice E1 de employee-payroll-extensions). Orquesta
 * `js/infrastructure/payroll-config.repository.js` (thin, SIN validar por
 * diseño — D2); TODA la validación vive acá, ZERO lógica de Supabase en
 * este módulo (mismo criterio que `admin-empleados.service.js`).
 *
 * Este editor es de PANTALLA (RLS, browser, anon key) — nunca es la lectura
 * autoritativa para liquidar sueldos, que vive server-side en
 * `api/liquidacion.js` (fase 1b, service-role). Un tope mal cargado acá
 * queda expuesto igual cuando el admin liquide (`tope_no_configurado`/
 * `error_interno` del lado del servidor), pero el rechazo temprano acá
 * evita el round-trip y — sobre todo — evita el error más peligroso de
 * todos: guardar un tope inválido como si fuera válido.
 */

/** `check (tope_descuento_mensual >= 0)` — el resguardo de la base, ver `payroll-config.repository.js:56`. */
const CHECK_VIOLATION = '23514';

/** `insufficient_privilege` — la RLS `payroll_config_admin_all` rechaza un rol sin `is_admin()` (F2, review-050ef9b16341fee9). */
const PERMISSION_DENIED = '42501';

/**
 * Mensaje EXACTO que arroja `payroll-config.repository.js#savePayrollConfig`
 * cuando no hay client de Supabase (ver su JSDoc) — es la señal, por texto y
 * no por código, que distingue "no se pudo conectar" de "el valor era
 * inválido". F2 (review-050ef9b16341fee9): antes de este fix este mensaje se
 * re-lanzaba y `handleLiquidacionCapSaveSubmit` lo colapsaba en el mismo
 * toast genérico de "probá de nuevo" que un rechazo transitorio.
 */
const REPO_CONFIG_UNAVAILABLE_MESSAGE = 'Configuración de liquidación no disponible';

/**
 * `code` que arroja `payroll-config.repository.js#savePeriodoPorDefecto`
 * cuando el UPDATE no encontró la fila singleton (el tope mensual nunca se
 * cargó, AD11). Su mensaje ya es el texto para el operador.
 */
const REPO_PAYROLL_CONFIG_MISSING = 'PAYROLL_CONFIG_MISSING';

/**
 * ¿El valor es "nada" (obligatorio ausente)? Un string todo espacios pasa
 * `Number(' ')` como `0` — sin este chequeo explícito, tipear solo espacios
 * en el input y enviar el form terminaría guardando un tope en $0 sin que
 * nadie lo haya escrito a propósito.
 * @param {*} value
 * @returns {boolean}
 */
function isBlank(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
}

/**
 * Un string "parece número" en JS acepta notaciones que un `<input
 * type="number">` normal jamás produce — hexadecimal, octal, binario,
 * notación científica — porque `Number()` las entiende todas:
 * `Number('0x10')` es `16`, así que un tope tipeado como `"0x10"` pasaba
 * TODOS los chequeos de abajo y se guardaba como `16` sin pasar nunca por el
 * rechazo de "debe ser un número" (F5, review-050ef9b16341fee9). Esta app
 * solo entiende decimal simple: cualquier string que no sea un literal
 * decimal (`Infinity`/`-Infinity` aparte, que ya tienen su propio mensaje
 * distinto más abajo) se trata como no-numérico.
 * @param {*} value
 * @returns {boolean}
 */
// Cheap follow-up (E1b review): a trailing-dot decimal ("5.", no fractional
// digits) is a literal `Number()` already coerces (`Number('5.') === 5`) —
// the old pattern required at least one digit after the dot, so it rejected
// a value the previous naive coercion silently accepted. Widened to `\d*`.
const PLAIN_DECIMAL_STRING = /^[-+]?(\d+\.?\d*|\.\d+)$/;
function isNonDecimalNumericString(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed === 'Infinity' || trimmed === '-Infinity' || trimmed === '+Infinity') return false;
    return !PLAIN_DECIMAL_STRING.test(trimmed);
}

/**
 * Valida el tope ANTES de tocar el repositorio (Req: Cap Configuration Is
 * Admin-Set and Server-Authoritative — "A missing, non-numeric, or negative
 * cap value MUST hard-stop"). Cinco rechazos, cada uno con su propio
 * mensaje accionable — nunca colapsados en un solo "valor inválido":
 * obligatorio, no-decimal (hex/octal/binario/científica — F5, mismo
 * mensaje que no-numérico porque para este validador no-decimal ES
 * no-numérico), no-numérico, no-finito (`Infinity`/`-Infinity`, que
 * `Number.isNaN` no atrapa) y negativo.
 *
 * NUNCA convierte un valor inválido a `0`: un tope en $0 recuperaría el
 * 100% de los consumos de TODOS los empleados en la próxima liquidación —
 * exactamente la clase de falla (recuperación silenciosa/incorrecta de un
 * excedente) que esta extensión entera existe para prevenir. Un rechazo
 * explícito es siempre preferible a un default que "funciona" pero miente.
 * @param {*} value
 * @returns {string|null} mensaje de error, o `null` si es válido
 */
function validateTopeDescuentoMensual(value) {
    if (isBlank(value)) {
        return 'El tope mensual de descuento es obligatorio.';
    }
    if (isNonDecimalNumericString(value)) {
        return 'El tope mensual de descuento debe ser un número.';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return 'El tope mensual de descuento debe ser un número.';
    }
    if (!Number.isFinite(num)) {
        return 'El tope mensual de descuento debe ser un valor finito.';
    }
    if (num < 0) {
        return 'El tope mensual de descuento no puede ser negativo.';
    }
    return null;
}

/**
 * @typedef {{ ok: true, tope: number|null, configurado: boolean, updatedAt: string|null, updatedBy: string|null }} PayrollConfigReadResult
 */

/**
 * Lee el tope vigente para mostrarlo en el editor.
 *
 * NULL AMBIGUO, a propósito documentado: el repositorio (D2, fuera de este
 * slice) devuelve `null` en DOS casos que NO puede distinguir entre sí —
 * "Supabase no está configurado" (degradación silenciosa, mismo criterio
 * que el resto del panel) y "AD11: la tabla existe pero nunca se sembró
 * ninguna fila, el owner todavía no cargó un tope". Este servicio NO
 * inventa una señal que el repositorio no expone (eso sería fabricar una
 * distinción falsa); en cambio, presenta AMBOS casos de forma honesta como
 * el MISMO estado — `configurado: false` — porque para el propósito de
 * ESTE editor (mostrar el valor vigente) el efecto correcto es idéntico en
 * los dos casos: no hay ningún número confiable para mostrar, y el
 * `configurado: false` nunca debe leerse como "$0". Si la causa real es
 * "Supabase no disponible", esa causa queda expuesta igual en cuanto el
 * admin intenta GUARDAR — a diferencia de la lectura, `savePayrollConfig`
 * (abajo) SÍ puede distinguirla, porque el repositorio la señaliza con un
 * `throw` propio en el camino de escritura.
 * @returns {Promise<PayrollConfigReadResult>}
 * @throws {Error} si el repositorio responde con un error real de Supabase (no degradación)
 */
export async function loadPayrollConfig() {
    const config = await repoGetPayrollConfig();
    if (!config) {
        // S4 (AD17): sin fila, `periodoPorDefecto` es `null` — el CALLER
        // (js/admin.js, hoy S2a, mañana S6) es quien decide el fallback a
        // 'mes', esta capa nunca inventa el default.
        return { ok: true, tope: null, configurado: false, updatedAt: null, updatedBy: null, periodoPorDefecto: null };
    }
    return {
        ok: true,
        tope: config.tope_descuento_mensual,
        configurado: true,
        // F4 (review-050ef9b16341fee9): el repositorio ya trae estas dos
        // columnas (D2, sin cambios) pero nadie las leía río abajo — dos
        // admins editando el mismo tope se pisaban en silencio, sin señal.
        updatedAt: config.updated_at ?? null,
        updatedBy: config.updated_by ?? null,
        // S4 (AD17): ausente en la fila (columna aún no cargada por ningún
        // admin) llega como `null` acá también — mismo criterio.
        periodoPorDefecto: config.periodo_por_defecto ?? null,
    };
}

/**
 * Guarda `periodo_por_defecto` (S4/AD17) — validado ANTES de tocar el
 * repositorio, mismo criterio que `savePayrollConfig`. `null` es un valor
 * VÁLIDO acá (vuelve la preferencia a "ausente" = 'mes'), a diferencia del
 * tope mensual, que rechaza ausencia — el tope es dinero, esto es una
 * preferencia de pantalla.
 * @param {{ periodoPorDefecto: 'mes'|'quincena'|'semana'|null, updatedBy: string|null }} params
 * @returns {Promise<{ ok: boolean, periodoPorDefecto?: string|null, error?: string }>}
 */
export async function savePeriodoPorDefecto({ periodoPorDefecto, updatedBy } = {}) {
    if (periodoPorDefecto !== null && !PERIODO_POR_DEFECTO_VALIDOS.includes(periodoPorDefecto)) {
        return { ok: false, error: 'El período por defecto debe ser "mes", "quincena" o "semana".' };
    }

    let config;
    try {
        config = await repoSavePeriodoPorDefecto({ periodoPorDefecto, updatedBy: updatedBy ?? null });
    } catch (err) {
        // Sin fila singleton: falla accionable (cargar el tope primero), no un
        // "probá de nuevo" genérico. Cualquier otro error se relanza tal cual.
        if (err?.code === REPO_PAYROLL_CONFIG_MISSING) return { ok: false, error: err.message };
        throw err;
    }
    return { ok: true, periodoPorDefecto: config.periodo_por_defecto ?? null };
}

/**
 * @typedef {{ ok: boolean, tope?: number, updatedAt?: string|null, updatedBy?: string|null, error?: string, permanente?: boolean }} SavePayrollConfigResult
 */

/**
 * Guarda el tope mensual de descuento (singleton `payroll_config`).
 *
 * F2 (review-050ef9b16341fee9): un rechazo de PERMISOS (`42501`, un rol sin
 * `is_admin()` alcanzando este editor) o una configuración inalcanzable
 * (`REPO_CONFIG_UNAVAILABLE_MESSAGE`, Supabase sin cliente) son fallas
 * PERMANENTES PARA ESTE INTENTO — nunca "el valor era inválido". Follow-up A
 * (review de E1b, 2026-08-10): NO son la misma clase de falla entre sí, y
 * esta nota antes lo decía mal ("nunca algo que reintentar arregla" para
 * ambas). El rechazo de permisos nunca lo arregla un reintento, sin
 * importar cuándo — hace falta que un admin cambie el rol. La configuración
 * inalcanzable SÍ puede ser transitoria (Supabase caído un momento), y por
 * eso su propio mensaje de abajo le dice al operador que reintente MÁS
 * TARDE — el mensaje siempre estuvo bien, era esta nota la que lo
 * contradecía. Antes de este fix ambos se relanzaban tal cual y el caller
 * los colapsaba en el mismo toast genérico de "probá de nuevo" que un
 * rechazo transitorio, lo que para un rol sin permiso significaba un
 * "reintentá" que nunca iba a funcionar. Acá se mapean explícitamente
 * (mismo criterio que el CHECK de abajo — el mapeo de código-a-mensaje vive
 * en el servicio, no en la UI), reportan a Sentry (follow-up C, mismo
 * lineage: al devolver `{ ok: false }` en vez de relanzar, dejaron de
 * llegar al `catch` del caller que hacía esa telemetría — las dos fallas
 * más diagnosticables del flujo que desbloquea todo el módulo se volvieron
 * invisibles) y vuelven con `permanente: true` para que el caller los
 * muestre de forma distinguible. Cualquier OTRO error (transporte cortado,
 * no clasificado) sigue relanzándose tal cual: nunca se traga un fallo
 * real.
 * @param {{ topeDescuentoMensual: number|string, updatedBy: string|null }} params
 * @returns {Promise<SavePayrollConfigResult>}
 */
export async function savePayrollConfig({ topeDescuentoMensual, updatedBy }) {
    const error = validateTopeDescuentoMensual(topeDescuentoMensual);
    if (error) return { ok: false, error };

    try {
        const config = await repoSavePayrollConfig({
            topeDescuentoMensual: Number(topeDescuentoMensual),
            updatedBy: updatedBy ?? null,
        });
        return {
            ok: true,
            tope: config.tope_descuento_mensual,
            updatedAt: config.updated_at ?? null,
            updatedBy: config.updated_by ?? null,
        };
    } catch (repoError) {
        // Último resguardo (el CHECK de la base, no el primero — la
        // validación de arriba ya debería haber atrapado esto).
        if (repoError?.code === CHECK_VIOLATION) {
            return { ok: false, error: 'El tope mensual de descuento no puede ser negativo.' };
        }
        if (repoError?.code === PERMISSION_DENIED) {
            // Follow-up C (review de E1b): reportar ANTES de volver — un
            // `return` acá nunca llega al `catch` del caller, que es donde
            // vivía la telemetría antes de F2.
            window.Sentry?.captureException(repoError);
            return {
                ok: false,
                error: 'Tu usuario no tiene permisos para modificar el tope mensual de descuento. Pedile a un administrador que lo actualice.',
                permanente: true,
            };
        }
        if (repoError?.message === REPO_CONFIG_UNAVAILABLE_MESSAGE) {
            window.Sentry?.captureException(repoError); // Follow-up C, mismo criterio.
            return {
                ok: false,
                error: 'No pudimos conectar con la configuración de liquidación. Probá de nuevo más tarde; si el problema sigue, avisá a un administrador.',
                permanente: true,
            };
        }
        throw repoError;
    }
}
